/**
 * ResendKilledControls — the entry points: "Re-send" / "Re-schedule" in the
 * Sent selection bar (only while killed drops are selected), and one button on
 * a killed row. Both open the same dialog, which is also the confirmation.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, type FC } from 'react';
import { CalendarClock, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

import { isKilled, isResendable, type ResendMode } from '@/features/resend-killed-drops/planResend';
import { ResendKilledDialog } from '@/features/resend-killed-drops/components/ResendKilledDialog';

export interface ResendKilledControlsProps {
  /** The selection (bar) or the one row — anything not killed is ignored. */
  drops: PreparedDrop[];
  variant?: 'bar' | 'row';
  disabled?: boolean;
  onDone?: () => void;
}

const ALREADY = 'Every selected killed drop was already re-sent';

export const ResendKilledControls: FC<ResendKilledControlsProps> = ({
  drops,
  variant = 'bar',
  disabled = false,
  onDone,
}) => {
  const [mode, setMode] = useState<ResendMode | null>(null);
  const killed = drops.filter(isKilled);
  const count = killed.filter(isResendable).length;
  if (killed.length === 0) return null;

  const dialog = mode && (
    <ResendKilledDialog drops={killed} initialMode={mode} onClose={() => setMode(null)} onDone={onDone} />
  );

  if (variant === 'row') {
    if (count === 0) return null;
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 shrink-0 p-0"
          disabled={disabled}
          onClick={() => setMode('now')}
          aria-label="Re-send killed drop"
          title="Re-send or re-schedule"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
        {dialog}
      </>
    );
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2"
        disabled={disabled || count === 0}
        onClick={() => setMode('now')}
        title={count === 0 ? ALREADY : 'Send the killed drops again as new drops, now'}
      >
        <RotateCcw className="h-4 w-4" />
        Re-send ({count})
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2"
        disabled={disabled || count === 0}
        onClick={() => setMode('original')}
        title={count === 0 ? ALREADY : 'Send the killed drops again as new drops, at a later time'}
      >
        <CalendarClock className="h-4 w-4" />
        Re-schedule ({count})
      </Button>
      {dialog}
    </>
  );
};
